import React, { useState, useEffect, useRef, useCallback } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useToast } from '../../contexts/ToastContext';
import fournisseurService from '../../services/fournisseurService';
import AddFournisseurDialog from '../../components/AddFournisseurDialog/AddFournisseurDialog';
import ViewFournisseurDialog from '../../components/ViewFournisseurDialog/ViewFournisseurDialog';
import EditFournisseurDialog from '../../components/EditFournisseurDialog/EditFournisseurDialog';
import {
    Truck,
    Plus,
    Search,
    Filter,
    Edit,
    Trash2,
    Phone,
    Mail,
    MapPin,
    Eye,
    Building2,
    X
} from 'lucide-react';
import './FournisseursPage.css';

const FournisseursPage = () => {
    const { t } = useTranslation();
    const toast = useToast();
    const isMobile = useIsMobile();
    const [fournisseurs, setFournisseurs] = useState([]);
    const [loading, setLoading] = useState(true);
    const { isResponsable, isAdmin } = useAuth();
    const { canCreate, canEdit, canDelete } = usePermissions();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const searchTimeoutRef = useRef(null);
    const [filterType, setFilterType] = useState('all');

    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isProductsDialogOpen, setIsProductsDialogOpen] = useState(false);
    const [selectedFournisseur, setSelectedFournisseur] = useState(null);
    const [fournisseurProducts, setFournisseurProducts] = useState([]);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 10;

    const fetchFournisseurs = async (page = 1) => {
        try {
            setLoading(true);
            const params = {
                page: page,
                page_size: pageSize,
            };
            if (debouncedSearchTerm) {
                params.search = debouncedSearchTerm;
            }
            const response = await fournisseurService.getAll(params);
            if (response.results) {
                setFournisseurs(response.results);
                setTotalCount(response.count);
                setTotalPages(Math.ceil(response.count / pageSize));
            } else {
                setFournisseurs(response || []);
                setTotalCount(response?.length || 0);
                setTotalPages(1);
            }
        } catch (error) {
            console.error('Error fetching fournisseurs:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFournisseurs(currentPage);
    }, [currentPage, debouncedSearchTerm]);

    const handleFournisseurAdded = (newFournisseur) => {
        fetchFournisseurs(currentPage);
    };

    const handleFournisseurUpdated = (updatedFournisseur) => {
        fetchFournisseurs(currentPage);
    };

    const handleDelete = async (item) => {
        const toastId = toast.custom(
            'danger',
            <div style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>
                    {t('fournisseurs.confirmDeleteTitle', { defaultValue: 'Supprimer ce fournisseur ?' })}
                </div>
                <div style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.9rem' }}>
                    <strong>{item?.name}</strong><br/>
                    {t('fournisseurs.confirmDeleteMessage', { defaultValue: 'Cette action est irréversible.' })}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1rem' }}>
                    <button
                        type="button"
                        className="toast-btn toast-btn-danger"
                        onClick={async () => {
                            toast.remove(toastId);
                            try {
                                await fournisseurService.delete(item.id);
                                setFournisseurs(prev => prev.filter(f => f.id !== item.id));
                                toast.success(t('fournisseurs.deleteSuccess', { defaultValue: 'Fournisseur supprimé.' }));
                            } catch (error) {
                                console.error('Error deleting fournisseur:', error);
                                const msg = error.response?.data?.detail || error.response?.data?.non_field_errors?.[0];
                                toast.error(msg || t('fournisseurs.deleteError'));
                            }
                        }}
                    >
                        {t('common.delete', { defaultValue: 'Supprimer' })}
                    </button>
                    <button
                        type="button"
                        className="toast-btn"
                        onClick={() => toast.remove(toastId)}
                    >
                        {t('common.cancel', { defaultValue: 'Annuler' })}
                    </button>
                </div>
            </div>,
            { duration: null }
        );
    };

    const openViewDialog = (fournisseur) => {
        setSelectedFournisseur(fournisseur);
        setIsViewDialogOpen(true);
    };

    const openEditDialog = (fournisseur) => {
        setSelectedFournisseur(fournisseur);
        setIsEditDialogOpen(true);
    };

    const openProductsDialog = async (fournisseur) => {
        setSelectedFournisseur(fournisseur);
        try {
            const response = await fournisseurService.getProducts(fournisseur.id);
            setFournisseurProducts(response.results || response || []);
        } catch (error) {
            console.error('Error fetching products:', error);
            setFournisseurProducts([]);
        }
        setIsProductsDialogOpen(true);
    };

    const handleCloseProductsDialog = () => {
        setIsProductsDialogOpen(false);
        setSelectedFournisseur(null);
        setFournisseurProducts([]);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchTerm(value);
            setCurrentPage(1);
        }, 3000);
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }
    };

    const handleSearchBlur = () => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        if (searchTerm !== debouncedSearchTerm) {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }
    };

    const handleFilterChange = (e) => {
        setFilterType(e.target.value);
        setCurrentPage(1);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    if (loading) {
        return (
            <div className="page-loading">
                <div className="loader"></div>
                <span>{t('fournisseurs.loading')}</span>
            </div>
        );
    }

    return (
        <div className="fournisseurs-page">
            <div className="page-header-section">
                <div className="header-titles">
                    <h1 className="page-title">
                        <Truck size={32} />
                        {t('fournisseurs.title')}
                    </h1>
                    <p className="page-subtitle">{t('fournisseurs.subtitle')}</p>
                </div>
                {canCreate('fournisseurs') && (
                    <button onClick={() => setIsAddDialogOpen(true)} className="btn-add">
                        <Plus size={20} />
                        {t('fournisseurs.add')}
                    </button>
                )}
            </div>

            <div className="filters-section">
                <div className="search-bar">
                    <Search size={20} className="search-icon" />
                    <input
                        type="text"
                        placeholder={t('fournisseurs.searchPlaceholder')}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyDown}
                        onBlur={handleSearchBlur}
                        className="search-input"
                    />
                    {searchTerm && (
                        <button 
                            className="search-clear-btn"
                            onClick={handleClearSearch}
                            title={t('common.clearSearch')}
                        >
                            <X size={16} />
                        </button>
                    )}
                </div>

                <div className="filter-group">
                    <Filter size={18} />
                    <select
                        value={filterType}
                        onChange={handleFilterChange}
                        className="filter-select"
                    >
                        <option value="all">{t('fournisseurs.filters.allTypes')}</option>
                        <option value="MATERIEL">{t('fournisseurs.filters.material')}</option>
                        <option value="FOURNITURE">{t('fournisseurs.filters.supplies')}</option>
                        <option value="SERVICE">{t('fournisseurs.filters.services')}</option>
                    </select>
                </div>
            </div>

            <div className="ui-fixed-list">
                <div className="ui-fixed-list-scroll overflow-auto">
                    <div className="table-container">
                        <table className={`data-table ${isMobile ? 'mobile-table' : ''}`}>
                            {isMobile ? (
                                <thead className="mobile-thead">
                                    <tr>
                                        <th className="mobile-name-col">{t('fournisseurs.table.name')}</th>
                                        <th className="mobile-actions-col">{t('fournisseurs.table.actions')}</th>
                                    </tr>
                                </thead>
                            ) : (
                                <thead>
                                    <tr>
                                        <th>{t('fournisseurs.table.name')}</th>
                                        <th>{t('fournisseurs.table.contact')}</th>
                                        <th>{t('fournisseurs.table.phone')}</th>
                                        <th>{t('fournisseurs.table.address')}</th>
                                        <th>{t('fournisseurs.table.products')}</th>
                                        <th className="actions-column">{t('fournisseurs.table.actions')}</th>
                                    </tr>
                                </thead>
                            )}
                            <tbody>
                                {fournisseurs.length === 0 ? (
                                    <tr>
                                        <td colSpan={isMobile ? 2 : 6} className="empty-state">
                                            <Truck size={48} className="empty-icon" />
                                            <h3>{t('fournisseurs.empty.title')}</h3>
                                            <p>{t('fournisseurs.empty.text')}</p>
                                        </td>
                                    </tr>
                                ) : (
                                    fournisseurs.map((item) => (
                                        isMobile ? (
                                            <tr key={item.id} className="mobile-row">
                                                <td className="mobile-name-cell">
                                                    <div className="mobile-fournisseur-name">{item.name}</div>
                                                </td>
                                                <td className="mobile-actions-cell">
                                                    <div className="mobile-actions-wrapper">
                                                        <button className="action-btn action-view" onClick={() => openViewDialog(item)} title={t('fournisseurs.actions.viewDetails')}><Eye size={16} /></button>
                                                        {canEdit('fournisseurs') && (
                                                            <button className="action-btn action-edit" onClick={() => openEditDialog(item)} title={t('common.edit')}><Edit size={16} /></button>
                                                        )}
                                                        {canDelete('fournisseurs') && (
                                                            <button className="action-btn action-delete" onClick={() => handleDelete(item)} title={t('fournisseurs.actions.delete')}><Trash2 size={16} /></button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            <tr key={item.id}>
                                                <td className="name-cell">
                                                    <div className="fournisseur-name">
                                                        <Building2 size={16} />
                                                        {item.name}
                                                    </div>
                                                </td>
                                                <td className="email-cell">
                                                    {item.contact_email ? (
                                                        <a href={`mailto:${item.contact_email}`} className="email-link">
                                                            {item.contact_email}
                                                        </a>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                                <td className="phone-cell">
                                                    {item.contact_phone ? (
                                                        <a href={`tel:${item.contact_phone}`} className="phone-link">
                                                            <Phone size={14} />
                                                            {item.contact_phone}
                                                        </a>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                                <td className="address-cell">
                                                    <span className="address-text">
                                                        {item.address ? (
                                                            <>
                                                                <MapPin size={14} />
                                                                {item.address.substring(0, 30)}
                                                                {item.address.length > 30 ? '...' : ''}
                                                            </>
                                                        ) : (
                                                            <span className="no-products">-</span>
                                                        )}
                                                    </span>
                                                </td>
                                                <td className="products-cell">
                                                    {(item.produits_count || 0) > 0 ? (
                                                        <button
                                                            className="products-link"
                                                            onClick={() => openProductsDialog(item)}
                                                            title={t('fournisseurs.actions.viewProducts')}
                                                        >
                                                            {t('fournisseurs.productsCount', { count: item.produits_count })}
                                                        </button>
                                                    ) : (
                                                        <span className="no-products">{t('fournisseurs.productsCount', { count: 0 })}</span>
                                                    )}
                                                </td>
                                                <td className="actions-cell">
                                                    <button className="action-btn action-view" onClick={() => openViewDialog(item)} title={t('fournisseurs.actions.viewDetails')}><Eye size={16} /></button>
                                                    {canEdit('fournisseurs') && (
                                                        <button className="action-btn action-edit" onClick={() => openEditDialog(item)} title={t('common.edit')}><Edit size={16} /></button>
                                                    )}
                                                    {canDelete('fournisseurs') && (
                                                        <button className="action-btn action-delete" onClick={() => handleDelete(item)} title={t('fournisseurs.actions.delete')}><Trash2 size={16} /></button>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="table-footer ui-fixed-list-footer">
                    <p className="results-count">
                        {t('fournisseurs.resultsCount', { total: totalCount, currentPage, totalPages })}
                    </p>
                    <div className="pagination">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="pagination-btn !border-green-200 !text-green-700 disabled:!text-gray-300 disabled:!border-gray-200"
                        >
                            {t('common.previous')}
                        </button>
                        <div className="page-numbers">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <button
                                    key={page}
                                    onClick={() => handlePageChange(page)}
                                    className={`page-number !border-green-200 !text-green-700 hover:!bg-green-50 ${page === currentPage ? 'active !bg-green-100 !border-green-400 !text-green-800 !font-bold' : '!bg-white'}`}
                                >
                                    {page}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="pagination-btn !border-green-200 !text-green-700 disabled:!text-gray-300 disabled:!border-gray-200"
                        >
                            {t('common.next')}
                        </button>
                    </div>
                </div>
            </div>

            <AddFournisseurDialog
                isOpen={isAddDialogOpen}
                onClose={() => setIsAddDialogOpen(false)}
                onFournisseurAdded={handleFournisseurAdded}
            />

            <ViewFournisseurDialog
                isOpen={isViewDialogOpen}
                onClose={() => {
                    setIsViewDialogOpen(false);
                    setSelectedFournisseur(null);
                }}
                fournisseur={selectedFournisseur}
            />

            <EditFournisseurDialog
                isOpen={isEditDialogOpen}
                onClose={() => {
                    setIsEditDialogOpen(false);
                    setSelectedFournisseur(null);
                }}
                fournisseur={selectedFournisseur}
                onFournisseurUpdated={handleFournisseurUpdated}
            />

            {isProductsDialogOpen && (
                <div className="dialog-overlay ui-dialog-overlay" onClick={handleCloseProductsDialog}>
                    <div className="dialog-content products-dialog ui-dialog-container" onClick={e => e.stopPropagation()}>
                        <div className="dialog-header ui-dialog-header">
                            <h2>{t('fournisseurs.productsDialog.title', { name: selectedFournisseur?.name || '' })}</h2>
                            <button className="dialog-close-btn" onClick={handleCloseProductsDialog}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="dialog-body">
                            {fournisseurProducts.length === 0 ? (
                                <p className="no-products-message">{t('fournisseurs.productsDialog.empty')}</p>
                            ) : (
                                <ul className="products-list">
                                    {fournisseurProducts.map(product => (
                                        <li key={product.id} className="product-item">
                                            <span className="product-name">{product.name}</span>
                                            <span className="product-price">{product.unit_price} {selectedFournisseur?.devise || 'EUR'}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FournisseursPage;
